import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { PAPER } from './theme';

/**
 * Site Diary cost tiles — Labour + Material, running spend-to-date only (no
 * per-day figure, owner decision 21/09/2026).
 * Data: GET /diary/projects/{id}/cost-summary?date=YYYY-MM-DD.
 *
 *   - Labour: To date = the diary running total (everyone on site × day rate,
 *     employees + sub-contractors); Confirmed = invoiced labour on the Cost
 *     Ledger. The project headline (Rule A) is employees' diary + invoiced —
 *     shown on the Labour log.
 *   - Materials & plant: every non-labour Cost Ledger row (material + plant/fuel
 *     + other); the tile is tappable and opens the project Cost Log (`onOpenLog`).
 *   - Admin + surveyor only — operatives NEVER see these numbers.
 */

const TILES = [
  { key: 'labour',   label: 'Labour'   },
  { key: 'material', label: 'Materials & plant' },
];

function fmtGbp(pounds) {
  const n = Number(pounds) || 0;
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function CostTotalsTiles({ projectId, date, refreshKey, onOpenLog }) {
  const { api, isAdmin, isSurveyor } = useAuth();
  const canSee = isAdmin || isSurveyor;
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!canSee || !projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const qs = date ? `?date=${date}` : '';
        const res = await api.get(`/diary/projects/${projectId}/cost-summary${qs}`);
        if (!cancelled) { setSummary(res.data); setError(false); }
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [api, projectId, date, canSee, refreshKey]);

  if (!canSee || error) return null;

  const cat = (key) => (summary ? summary[key] : null);

  return (
    <div className="grid grid-cols-2 gap-2.5 mt-3" data-testid="cost-totals-tiles">
      {TILES.map(({ key, label }) => {
        const c = cat(key);
        const missing = key === 'labour' ? (c?.day_missing_rate || 0) : 0;
        const tappable = typeof onOpenLog === 'function';
        const Tag = tappable ? 'button' : 'div';
        return (
          <Tag
            key={key}
            type={tappable ? 'button' : undefined}
            onClick={tappable ? () => onOpenLog(key) : undefined}
            className={`rounded-xl px-3 py-3 text-left flex flex-col justify-start ${tappable ? 'transition-transform active:scale-[0.98] hover:shadow-md' : ''}`}
            style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: PAPER.shadow }}
            data-testid={`cost-total-tile-${key}`}
            title={tappable ? `View the ${label.toLowerCase()} log` : undefined}
          >
            <div
              className="text-[10px] uppercase tracking-wider font-semibold mb-2 text-center"
              style={{ color: PAPER.muted }}
            >
              {label}
            </div>

            <div className="text-center">
              <div className="text-[9px] uppercase tracking-wide" style={{ color: PAPER.hint }}>{key === 'labour' ? 'To date · diary' : 'To date'}</div>
              <div
                className="text-[15px] font-bold tabular-nums"
                style={{ color: PAPER.accent }}
                data-testid={`cost-total-tile-${key}-value`}
              >
                {c ? fmtGbp(key === 'labour' ? (c.diary_all ?? c.total) : c.total) : '—'}
              </div>
            </div>

            {key === 'labour' && (
              <div className="text-center mt-1.5 pt-1.5" style={{ borderTop: `1px solid ${PAPER.divider}` }}>
                <div className="text-[9px] uppercase tracking-wide" style={{ color: PAPER.hint }}>Confirmed · invoiced</div>
                <div className="text-[15px] font-bold tabular-nums" style={{ color: '#0369A1' }} data-testid="cost-total-tile-labour-invoiced">
                  {c ? fmtGbp(c.invoiced ?? c.ledger) : '—'}
                </div>
              </div>
            )}
            {key === 'material' && c && (
              <div className="text-center mt-1.5 pt-1.5 text-[10px] tabular-nums" style={{ borderTop: `1px solid ${PAPER.divider}`, color: PAPER.muted }} data-testid="cost-total-tile-material-split">
                Mat {fmtGbp(c.material_only ?? 0)} · Plant {fmtGbp(c.plant ?? 0)} · Other {fmtGbp(c.other ?? 0)}
              </div>
            )}

            {missing > 0 && (
              <div
                className="mt-1.5 text-[9px] text-center leading-tight"
                style={{ color: '#B45309' }}
                data-testid={`cost-total-tile-${key}-missing`}
              >
                {missing} need a rate
              </div>
            )}

            {tappable && (
              <div
                className="mt-1.5 text-[10px] font-medium flex items-center justify-center gap-0.5"
                style={{ color: PAPER.inkSoft }}
                data-testid={`cost-total-tile-${key}-log-hint`}
              >
                View {key} log <ChevronRight className="w-3 h-3" />
              </div>
            )}
          </Tag>
        );
      })}
    </div>
  );
}

export default CostTotalsTiles;
